// File cards / list rows, skeletons and "Start a new …" tiles for the home
// screen (identical in Docs, Sheets and Slides).

import { useEffect, useRef, useState, type ComponentType, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import {
    Copy, ExternalLink, File as FileGlyph, FileSpreadsheet, FileText, FileType2, FolderOpen, HardDrive,
    Image as ImageGlyph, LayoutGrid, Loader2, MoreVertical, Pencil, Plus, Presentation, Star, StarOff, Trash2
} from "lucide-react";
import { homeApi } from "./homeApi";
import {
    classify, isShared, ownerLabel, timeLabel,
    type FileKind, type HomeConfig, type HomeItem, type HomeTemplate, type PreviewBlock, type SortKey, type SuiteTemplate
} from "./homeModel";
import { ActionMenu, cx, type MenuAction } from "./HomeUi";

type Glyph = ComponentType<{ className?: string; style?: CSSProperties }>;

export const KIND_STYLE: Record<FileKind, { label: string; icon: Glyph; tile: string; soft: string; text: string }> = {
    doc: { label: "Document", icon: FileText, tile: "bg-sky-600", soft: "bg-sky-50 dark:bg-sky-950/40", text: "text-sky-600 dark:text-sky-400" },
    sheet: { label: "Spreadsheet", icon: FileSpreadsheet, tile: "bg-emerald-600", soft: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-600 dark:text-emerald-400" },
    slide: { label: "Presentation", icon: Presentation, tile: "bg-orange-600", soft: "bg-orange-50 dark:bg-orange-950/40", text: "text-orange-600 dark:text-orange-400" },
    pdf: { label: "PDF", icon: FileType2, tile: "bg-rose-600", soft: "bg-rose-50 dark:bg-rose-950/40", text: "text-rose-600 dark:text-rose-400" },
    image: { label: "Image", icon: ImageGlyph, tile: "bg-violet-600", soft: "bg-violet-50 dark:bg-violet-950/40", text: "text-violet-600 dark:text-violet-400" },
    other: { label: "File", icon: FileGlyph, tile: "bg-slate-500", soft: "bg-slate-100 dark:bg-slate-800/60", text: "text-slate-500 dark:text-slate-400" }
};

export interface ItemHandlers {
    open: (item: HomeItem) => void;
    openNewTab: (item: HomeItem) => void;
    rename: (item: HomeItem) => void;
    copy: (item: HomeItem) => void;
    toggleStar: (item: HomeItem) => void;
    trash: (item: HomeItem) => void;
    openInDrive: (item: HomeItem) => void;
}

/**
 * The ⋯ menu. Drive only lets the owner star or bin a file and an owner or
 * editor rename it; a copy needs download rights.
 */
function menuFor(item: HomeItem, h: ItemHandlers): MenuAction[] {
    const shared = isShared(item);
    const groups: MenuAction[][] = [
        [
            { id: "open", label: "Open", icon: FolderOpen, onSelect: () => h.open(item) },
            { id: "open-tab", label: "Open in new tab", icon: ExternalLink, onSelect: () => h.openNewTab(item) }
        ],
        [
            ...(!shared || item.role === "editor"
                ? [{ id: "rename", label: "Rename", icon: Pencil, onSelect: () => h.rename(item) }]
                : []),
            ...(item.canDownload !== false
                ? [{ id: "copy", label: "Make a copy", icon: Copy, onSelect: () => h.copy(item) }]
                : []),
            ...(!shared
                ? [{ id: "star", label: item.isFavorite ? "Unstar" : "Star", icon: item.isFavorite ? StarOff : Star, onSelect: () => h.toggleStar(item) }]
                : [])
        ],
        [
            { id: "drive", label: "Open in Drive", icon: HardDrive, onSelect: () => h.openInDrive(item) },
            ...(!shared
                ? [{ id: "trash", label: "Move to bin", icon: Trash2, danger: true, onSelect: () => h.trash(item) }]
                : [])
        ]
    ];
    return groups
        .filter((group) => group.length > 0)
        .flatMap((group, gi) => group.map((action, ai) => ({ ...action, separatorBefore: gi > 0 && ai === 0 })));
}

function columnCount(cards: HTMLElement[]): number {
    const first = cards[0];
    if (!first) return 1;
    const top = first.getBoundingClientRect().top;
    return Math.max(1, cards.filter((c) => Math.abs(c.getBoundingClientRect().top - top) < 2).length);
}

/**
 * Grid or list of files. One card is in the tab order at a time (roving
 * tabindex): arrow keys move between cards, Home / End jump, Enter opens.
 */
export function FileCollection({
    items, view, sort, token, handlers
}: {
    items: HomeItem[];
    view: "grid" | "list";
    sort: SortKey;
    token: string | undefined;
    handlers: ItemHandlers;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const currentId = items.some((i) => i.id === activeId) ? activeId : (items[0]?.id ?? null);

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.dataset.cardIndex === undefined) return;
        const cards = Array.from(containerRef.current?.querySelectorAll<HTMLElement>("[data-card-index]") ?? []);
        const from = Number(target.dataset.cardIndex);
        const cols = view === "grid" ? columnCount(cards) : 1;
        let to: number;
        switch (e.key) {
            case "ArrowRight": if (view !== "grid") return; to = from + 1; break;
            case "ArrowLeft": if (view !== "grid") return; to = from - 1; break;
            case "ArrowDown": to = from + cols; break;
            case "ArrowUp": to = from - cols; break;
            case "Home": to = 0; break;
            case "End": to = cards.length - 1; break;
            default: return;
        }
        e.preventDefault();
        const clamped = Math.min(Math.max(to, 0), cards.length - 1);
        cards[clamped]?.focus();
        setActiveId(items[clamped]?.id ?? null);
    };

    if (view === "list") {
        return (
            <div ref={containerRef} onKeyDown={onKeyDown} className="overflow-hidden rounded-lg border bg-card text-card-foreground">
                <div aria-hidden className="hidden grid-cols-[minmax(0,1fr)_12rem_11rem_5rem] gap-3 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground sm:grid">
                    <span>Name</span>
                    <span>Owner</span>
                    <span>{sort === "modified" ? "Last modified" : "Last opened"}</span>
                    <span />
                </div>
                <div role="list" aria-label="Files">
                    {items.map((item, index) => (
                        <ListRow
                            key={item.id}
                            item={item}
                            index={index}
                            sort={sort}
                            active={item.id === currentId}
                            onFocus={() => setActiveId(item.id)}
                            handlers={handlers}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            role="list"
            aria-label="Files"
            onKeyDown={onKeyDown}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
            {items.map((item, index) => (
                <GridCard
                    key={item.id}
                    item={item}
                    index={index}
                    sort={sort}
                    token={token}
                    active={item.id === currentId}
                    onFocus={() => setActiveId(item.id)}
                    handlers={handlers}
                />
            ))}
        </div>
    );
}

interface CardProps {
    item: HomeItem;
    index: number;
    sort: SortKey;
    active: boolean;
    onFocus: () => void;
    handlers: ItemHandlers;
}

function describe(item: HomeItem, sort: SortKey): string {
    const kind = KIND_STYLE[classify(item.name, item.contentType)].label;
    const access = item.role === "viewer" ? ", view only" : "";
    return `${item.name}, ${kind}, ${timeLabel(item, sort)}, owner ${ownerLabel(item)}${access}`;
}

function CardActions({ item, handlers, tabIndex }: { item: HomeItem; handlers: ItemHandlers; tabIndex: number }) {
    return (
        <>
            {!isShared(item) ? (
                <button
                    type="button"
                    tabIndex={tabIndex}
                    aria-pressed={Boolean(item.isFavorite)}
                    aria-label={item.isFavorite ? `Unstar ${item.name}` : `Star ${item.name}`}
                    title={item.isFavorite ? "Unstar" : "Star"}
                    onClick={(e) => { e.stopPropagation(); handlers.toggleStar(item); }}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <Star className={cx("h-4 w-4", item.isFavorite && "fill-amber-400 text-amber-500")} aria-hidden />
                </button>
            ) : null}
            <ActionMenu
                label={`More actions for ${item.name}`}
                actions={menuFor(item, handlers)}
                tabIndex={tabIndex}
                triggerClassName="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <MoreVertical className="h-4 w-4" aria-hidden />
            </ActionMenu>
        </>
    );
}

function AccessBadge({ item }: { item: HomeItem }) {
    if (!isShared(item)) return null;
    return (
        <span className="absolute left-2 top-2 rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground shadow-sm">
            {item.role === "viewer" ? "View only" : "Shared"}
        </span>
    );
}

function GridCard({ item, index, sort, token, active, onFocus, handlers }: CardProps & { token: string | undefined }) {
    const kind = classify(item.name, item.contentType);
    const style = KIND_STYLE[kind];
    const Icon = style.icon;
    const fallback = (
        <span className={cx("flex h-full w-full items-center justify-center", style.soft)}>
            <Icon className={cx("h-10 w-10", style.text)} aria-hidden />
        </span>
    );
    return (
        <div
            role="listitem"
            className="group relative flex min-w-0 flex-col overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring"
        >
            <button
                type="button"
                data-card-index={index}
                tabIndex={active ? 0 : -1}
                onFocus={onFocus}
                onClick={() => handlers.open(item)}
                aria-label={describe(item, sort)}
                className="flex min-w-0 flex-col text-left focus-visible:outline-none"
            >
                <span className="relative block h-28 w-full overflow-hidden border-b sm:h-32">
                    {kind === "image" && item.canDownload !== false
                        ? <ImageThumb id={item.id} token={token} fallback={fallback} />
                        : fallback}
                    <AccessBadge item={item} />
                </span>
                <span className="flex min-w-0 items-center gap-2 px-3 pt-2.5">
                    <Icon className={cx("h-4 w-4 shrink-0", style.text)} aria-hidden />
                    <span className="truncate text-sm font-medium group-hover:underline">{item.name}</span>
                </span>
                <span className="block truncate px-3 pb-3 pr-[4.75rem] pt-1 text-xs text-muted-foreground">
                    {timeLabel(item, sort)}
                    <span className="block truncate">{ownerLabel(item)}</span>
                </span>
            </button>
            <div className="absolute bottom-1.5 right-1.5 flex items-center">
                <CardActions item={item} handlers={handlers} tabIndex={active ? 0 : -1} />
            </div>
        </div>
    );
}

function ListRow({ item, index, sort, active, onFocus, handlers }: CardProps) {
    const kind = classify(item.name, item.contentType);
    const style = KIND_STYLE[kind];
    const Icon = style.icon;
    return (
        <div role="listitem" className="group flex items-center border-b last:border-b-0 hover:bg-accent/50 focus-within:bg-accent/50">
            <button
                type="button"
                data-card-index={index}
                tabIndex={active ? 0 : -1}
                onFocus={onFocus}
                onClick={() => handlers.open(item)}
                aria-label={describe(item, sort)}
                className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] items-center gap-3 py-2 pl-3 text-left focus-visible:outline-none sm:grid-cols-[minmax(0,1fr)_12rem_11rem]"
            >
                <span className="flex min-w-0 items-center gap-3">
                    <span className={cx("flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white", style.tile)}>
                        <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0">
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm font-medium group-hover:underline">{item.name}</span>
                            {item.role === "viewer" ? (
                                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    View only
                                </span>
                            ) : null}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground sm:hidden">
                            {timeLabel(item, sort)} · {ownerLabel(item)}
                        </span>
                    </span>
                </span>
                <span className="hidden truncate text-sm text-muted-foreground sm:block">{ownerLabel(item)}</span>
                <span className="hidden truncate text-sm text-muted-foreground sm:block">{timeLabel(item, sort)}</span>
            </button>
            <div className="flex w-[5rem] shrink-0 items-center justify-end pr-1.5">
                <CardActions item={item} handlers={handlers} tabIndex={active ? 0 : -1} />
            </div>
        </div>
    );
}

function ImageThumb({ id, token, fallback }: { id: string; token: string | undefined; fallback: ReactNode }) {
    const [url, setUrl] = useState<string | null>(null);
    const tokenRef = useRef(token);
    tokenRef.current = token;

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;
        homeApi.thumbnail(id, tokenRef.current)
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => { /* no thumbnail — keep the icon */ });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [id]);

    return url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <>{fallback}</>;
}

export function SkeletonCollection({ view }: { view: "grid" | "list" }) {
    if (view === "list") {
        return (
            <div aria-hidden className="overflow-hidden rounded-lg border bg-card">
                {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0">
                        <span className="h-8 w-8 animate-pulse rounded-md bg-muted" />
                        <span className="h-3.5 flex-1 animate-pulse rounded bg-muted" />
                        <span className="hidden h-3 w-40 animate-pulse rounded bg-muted sm:block" />
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div aria-hidden className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {Array.from({ length: 10 }, (_, i) => (
                <div key={i} className="overflow-hidden rounded-lg border bg-card">
                    <div className="h-28 animate-pulse bg-muted sm:h-32" />
                    <div className="space-y-2 p-3">
                        <div className="h-3.5 w-4/5 animate-pulse rounded bg-muted" />
                        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── "Start a new …" tiles ────────────────────────────────────────────────

const BLOCKS: Record<PreviewBlock, (accent: string, key: number) => ReactNode> = {
    title: (accent, key) => <span key={key} className="block h-2 w-3/4 rounded-sm" style={{ backgroundColor: accent }} />,
    subtitle: (_accent, key) => <span key={key} className="block h-1.5 w-1/2 rounded-sm bg-slate-300" />,
    heading: (accent, key) => <span key={key} className="block h-1.5 w-2/5 rounded-sm opacity-80" style={{ backgroundColor: accent }} />,
    text: (_accent, key) => <span key={key} className="block h-1 w-full rounded-sm bg-slate-200" />,
    short: (_accent, key) => <span key={key} className="block h-1 w-3/5 rounded-sm bg-slate-200" />,
    bullet: (accent, key) => (
        <span key={key} className="flex items-center gap-1">
            <span className="h-1 w-1 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
            <span className="h-1 flex-1 rounded-sm bg-slate-200" />
        </span>
    ),
    table: (accent, key) => (
        <span key={key} className="grid grid-cols-3 gap-px rounded-sm bg-slate-300 p-px">
            {Array.from({ length: 9 }, (_, i) => (
                <span key={i} className="h-1.5" style={{ backgroundColor: i < 3 ? accent : "#ffffff" }} />
            ))}
        </span>
    ),
    chart: (accent, key) => (
        <span key={key} className="flex h-6 items-end gap-0.5">
            {[40, 70, 55, 90, 65].map((h, i) => (
                <span key={i} className="w-1.5 rounded-t-sm" style={{ height: `${h}%`, backgroundColor: accent, opacity: 0.55 + i * 0.09 }} />
            ))}
        </span>
    ),
    gap: (_accent, key) => <span key={key} className="block h-1.5" />
};

const SHEET_GRID: CSSProperties = {
    backgroundImage:
        "linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)",
    backgroundSize: "14px 9px"
};

function Paper({
    thumb, children, style, flush = false, align = "start"
}: {
    thumb: HomeConfig["thumb"];
    children: ReactNode;
    /** Overrides — e.g. a coloured slide background. */
    style?: CSSProperties;
    /** No inner padding (art that bleeds to the edges). */
    flush?: boolean;
    align?: "start" | "center";
}) {
    const shape =
        thumb === "page" ? "h-full aspect-[3/4]"
        : thumb === "slide" ? "w-full aspect-video"
        : "h-full w-full";
    const pad = flush ? "p-0" : thumb === "sheet" ? "p-1.5" : "p-2";
    return (
        <span
            className={cx(
                "relative flex flex-col gap-1 overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm dark:border-slate-600",
                align === "center" ? "justify-center" : "justify-start",
                shape,
                pad
            )}
            style={thumb === "sheet" ? { ...SHEET_GRID, ...style } : style}
        >
            {children}
        </span>
    );
}

/** A built-in (offline fallback) template's stylised preview. */
export function BuiltinTemplateArt({ template, thumb }: { template: HomeTemplate; thumb: HomeConfig["thumb"] }) {
    return <Paper thumb={thumb}>{template.preview.blocks.map((block, i) => BLOCKS[block](template.preview.accent, i))}</Paper>;
}

const safeColor = (hex: string) => (/^#[0-9a-f]{3,8}$/i.test(hex) ? hex : "#6b7785");
const tint = (c: string, pct: number) => `color-mix(in srgb, ${c} ${pct}%, #fff)`;

/** One grey text line of a miniature (width in %). */
const L = (w: number, style?: CSSProperties) => (
    <span className="block h-1 shrink-0 rounded-sm bg-slate-200" style={{ width: `${w}%`, ...style }} />
);
const GAP = (h = 4) => <span className="block shrink-0" style={{ height: h }} />;

/**
 * The CSS-drawn miniature of a shared-library template — the same art
 * Drive's "New" gallery draws from `thumb` + `accent`, fitted to this app's
 * thumbnail shape (page / grid / 16:9 slide). The paper stays white in dark
 * mode, like a real page.
 */
export function SuiteTemplateArt({ t, thumb }: { t: SuiteTemplate; thumb: HomeConfig["thumb"] }) {
    const c = safeColor(t.accent);

    if (thumb === "sheet") {
        return (
            <Paper thumb="sheet" flush>
                <span className="absolute left-1.5 top-1 block h-1.5 w-2/5 rounded-sm bg-slate-800 opacity-70" />
                <span className="absolute inset-x-0 top-3.5 block h-2.5" style={{ backgroundColor: c }} />
                {t.thumb === "grid-chart" ? (
                    <span className="absolute bottom-2 right-2 flex h-2/5 w-2/5 items-end gap-0.5 rounded-sm bg-white/90 p-1 shadow-sm">
                        {[50, 80, 64, 96].map((h, i) => (
                            <span key={i} className="flex-1 rounded-t-sm" style={{ height: `${h}%`, backgroundColor: c }} />
                        ))}
                    </span>
                ) : null}
            </Paper>
        );
    }

    if (thumb === "slide") {
        const [background, fg, sub] =
            t.thumb === "slide-dark" ? ["#1b1f24", c, "#aab2bd"]
            : t.thumb === "slide-teal" ? [tint(c, 12), `color-mix(in srgb, ${c} 80%, #000)`, tint(c, 55)]
            : t.thumb === "slide-violet" ? [`linear-gradient(160deg, ${c}, ${tint(c, 55)})`, "#fff", "rgba(255,255,255,.8)"]
            : [`linear-gradient(135deg, #fff 0 60%, ${tint(c, 16)} 60%)`, c, "#9aa3ad"];
        return (
            <Paper thumb="slide" align="center" style={{ background }}>
                <span className="block h-2 w-3/5 rounded-sm" style={{ background: fg }} />
                <span className="block h-1 w-2/5 rounded-sm opacity-70" style={{ background: sub }} />
            </Paper>
        );
    }

    switch (t.thumb) {
        case "letter":
            return (
                <Paper thumb="page">
                    <span className="block h-2 w-[30%] shrink-0 rounded-sm" style={{ backgroundColor: c }} />
                    {L(40)}{L(34)}{GAP(6)}{L(100)}{L(96)}{L(88)}{L(100)}{L(70)}{GAP()}{L(100)}{L(92)}{L(60)}
                </Paper>
            );
        case "invoice":
            return (
                <Paper thumb="page">
                    <span className="-mx-2 -mt-2 mb-1 block h-3.5 shrink-0" style={{ backgroundColor: c }} />
                    {L(40)}{L(30)}
                    <span className="block h-3 shrink-0 rounded-sm" style={{ backgroundColor: tint(c, 18) }} />
                    {L(100)}{L(100)}{L(100)}
                    {L(36, { marginLeft: "auto", height: 5, backgroundColor: c })}
                </Paper>
            );
        case "resume":
            return (
                <Paper thumb="page" flush>
                    <span className="flex h-full">
                        <span className="h-full w-[34%] shrink-0" style={{ backgroundColor: c }} />
                        <span className="flex min-w-0 flex-1 flex-col gap-1 px-1.5 py-2.5">
                            <span className="block h-2 w-[70%] shrink-0 rounded-sm bg-slate-800" />
                            {L(50)}{GAP()}{L(40, { backgroundColor: c })}{L(100)}{L(90)}{L(95)}{L(40, { backgroundColor: c })}{L(100)}{L(80)}
                        </span>
                    </span>
                </Paper>
            );
        case "report":
            return (
                <Paper thumb="page" flush>
                    <span className="flex h-[58%] shrink-0 flex-col justify-end gap-1 p-2" style={{ backgroundColor: c }}>
                        <span className="block h-2 w-[70%] rounded-sm bg-white" />
                        <span className="block h-1 w-2/5 rounded-sm bg-white opacity-70" />
                    </span>
                    <span className="flex flex-col gap-1 p-2">{L(100)}{L(84)}{L(92)}</span>
                </Paper>
            );
        default:
            return (
                <Paper thumb="page">
                    <span className="block h-2 w-[55%] shrink-0 rounded-sm" style={{ backgroundColor: c }} />
                    {L(50)}{GAP()}{L(100)}{L(90)}{L(100)}{L(76)}
                    <span className="block h-3 shrink-0 rounded-sm" style={{ backgroundColor: tint(c, 14) }} />
                    {L(100)}{L(64)}
                </Paper>
            );
    }
}

const TILE = "group flex w-32 shrink-0 snap-start flex-col gap-1.5 rounded-lg text-left focus-visible:outline-none sm:w-36";
const TILE_BOX =
    "relative flex h-36 w-full items-center justify-center rounded-lg border p-2.5 transition-colors group-hover:border-primary group-focus-visible:ring-2 group-focus-visible:ring-ring sm:h-40";

export function TemplateTile({
    label, sublabel, title, art, thumb, accentText, accentSoft, busy = false, disabled = false, onClick
}: {
    label: string;
    sublabel?: string;
    title?: string;
    /** The thumbnail (BuiltinTemplateArt / SuiteTemplateArt); undefined = the Blank tile. */
    art?: ReactNode;
    thumb: HomeConfig["thumb"];
    accentText: string;
    accentSoft: string;
    /** Creating the file from this tile. */
    busy?: boolean;
    /** Another tile is busy. */
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
            title={title ?? label}
            className={cx(TILE, "disabled:cursor-wait", disabled && !busy && "opacity-60")}
        >
            <span className={cx(TILE_BOX, art ? "bg-muted/40" : accentSoft)}>
                {art ?? (
                    <Paper thumb={thumb}>
                        <span className="flex flex-1 items-center justify-center">
                            <Plus className={cx("h-10 w-10", accentText)} aria-hidden />
                        </span>
                    </Paper>
                )}
                {busy ? (
                    <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/70">
                        <Loader2 className={cx("h-7 w-7 animate-spin", accentText)} aria-hidden />
                        <span className="sr-only">Creating…</span>
                    </span>
                ) : null}
            </span>
            <span className="truncate px-0.5 text-sm font-medium">{label}</span>
            {sublabel ? <span className="-mt-1 truncate px-0.5 text-xs text-muted-foreground">{sublabel}</span> : null}
        </button>
    );
}

export function TemplateTileSkeleton() {
    return (
        <span aria-hidden className="flex w-32 shrink-0 flex-col gap-1.5 sm:w-36">
            <span className="h-36 animate-pulse rounded-lg bg-muted sm:h-40" />
            <span className="h-3.5 w-3/4 animate-pulse rounded bg-muted" />
            <span className="h-3 w-1/2 animate-pulse rounded bg-muted" />
        </span>
    );
}

/** Last tile of the row: the full gallery lives in Drive. */
export function MoreTemplatesTile({ href }: { href: string }) {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" title="Open Drive — New shows every template in the suite" className={TILE}>
            <span className={cx(TILE_BOX, "flex-col gap-2 border-dashed bg-background text-muted-foreground group-hover:text-foreground")}>
                <LayoutGrid className="h-8 w-8" aria-hidden />
                <span className="flex items-center gap-1 text-xs font-medium">
                    Open Drive <ExternalLink className="h-3 w-3" aria-hidden />
                </span>
            </span>
            <span className="truncate px-0.5 text-sm font-medium">More templates</span>
            <span className="-mt-1 truncate px-0.5 text-xs text-muted-foreground">Every app's templates</span>
        </a>
    );
}
